/**
 * A árvore de estrutura do Faturômetro e os totais dela.
 *
 * Regra nº 1 da reconstrução: nenhum número pode mudar. Este arquivo é onde ela
 * pode ser quebrada sem ninguém ver — um erro aqui não dá erro de tela, dá
 * número errado. Por isso ele é JS puro, sem Vue, e tem teste próprio rodável
 * por `yarn test:contract`.
 *
 * O QUE VEM DE ONDE
 *
 *   /api/consultant_performance/table      linhas com número, chaveadas por
 *                                          (consultant_id, digital_franchise_id)
 *   /api/consultant_performance/structure  o esqueleto: pai, caminho de
 *                                          ancestrais, nível e papel. Sem número.
 *
 * São duas chamadas de propósito: a linha da tabela é o que o comparador de
 * paridade do backend confere campo a campo, e a estrutura muda de mês em mês
 * enquanto o número muda a cada filtro.
 *
 * POR QUE OS TOTAIS NÃO PODEM SER SOMA DE RAZÕES
 *
 * O backend devolve uma linha por consultor por dia e soma os dias. Razões como
 * EA/EF, EF/EP e LTV/CL não sobrevivem a soma: média de médias não é média.
 * Elas têm de ser calculadas a partir dos totais que saem daqui, nunca somadas
 * linha a linha — e quem as calcula é kpiColumns.js, que guarda as fórmulas
 * exatas da tela antiga. Este arquivo só soma.
 */

/** Os campos que a tela soma hoje, exatamente esses. Espelha KpiTotalizers.vue. */
const SUM_FIELDS = [
  'hot_leads_quantity',
  'scheduled_interview_quantity',
  'pre_interviews_quantity',
  'interviews_quantity',
  // Contagem DISTINTA, somada pela árvore. A soma superconta o cliente que
  // comprou de mais de um consultor no período — medido: 0 casos em junho/2026
  // e 40 de 1.357 no ano inteiro (2,9%). Como a tela lê uma competência por
  // vez, na prática é exato; o desvio só aparece em recorte longo.
  'customers_quantity',
  'interview_contract_quantity',
  'negotiation_interview_contract_quantity',
  'done_interview_quantity',
  'client_leads_quantity',
  'consultant_leads_quantity',
  'scheduled_x_moment_quantity',
  'done_x_moment_quantity',
  'x_moment_positive_feedbacks_quantity',
  'scheduled_partnership_quantity',
  'done_partnership_quantity',
  'partnership_positive_feedbacks_quantity',
  'interview_contract_amount',
  'subscription_revenue_reconciled',
  'renewal_contract_amount',
  'other_financial_solutions_contract_amount',
  'total_contract_amount',
  'support_contract_quantity',
  'signature_contract_quantity',
  'scheduled_negotiation_quantity',
  'negotiation_positive_feedbacks_quantity',
  'renewal_contract_quantity',
]

const toNumber = value => {
  const number = Number(value)
  return Number.isFinite(number) ? number : 0
}

const rowKey = row => `${toNumber(row.consultant_id || row.id)}:${toNumber(row.digital_franchise_id)}`

const emptyTotals = () => {
  const totals = {}
  SUM_FIELDS.forEach(field => {
    totals[field] = 0
  })
  return totals
}

/** Soma dois conjuntos de totais sem tocar em nenhum dos dois. */
const addTotals = (first, second) => {
  const totals = {}
  SUM_FIELDS.forEach(field => {
    totals[field] = toNumber(first[field]) + toNumber(second[field])
  })
  return totals
}

/**
 * Profundidade e numeração posicional, numa passada só.
 *
 * Por iteração e não por recursão: a árvore chega a 2.700 nós.
 *
 * SOBRE A NUMERAÇÃO (`outline`)
 *
 * É posição no que está na tela — 1, 1.1, 1.1.2 —, derivada da árvore montada
 * neste recorte. NÃO é código oficial de ninguém: muda quando o filtro muda, e
 * é por isso que a coluna que a exibe diz "posição" e vem desligada. Serve para
 * uma pessoa dizer "olha a 2.3.1" para outra na mesma tela, que é o uso que a
 * ata descrevia. Se existir numeração oficial na empresa, ela vem do backend e
 * substitui esta — o campo continua sendo `outline`, a fonte é que muda.
 */
const assignPositions = roots => {
  const stack = roots.map((item, index) => ({ node: item, depth: 0, outline: String(index + 1) }))
  while (stack.length) {
    const { node, depth, outline } = stack.pop()
    node.depth = depth
    node.outline = outline
    node.children.forEach((child, index) => stack.push({
      node: child,
      depth: depth + 1,
      outline: `${outline}.${index + 1}`,
    }))
  }
}

/**
 * Subtotal de cada nó = o próprio + a soma dos filhos, cada consultor contado
 * UMA vez.
 *
 * A garantia vem de haver exatamente um nó por (consultor, franquia) — o
 * backend prova isso em ConsultantStructureServiceTest. Se um consultor
 * aparecesse pendurado em dois pais, o faturamento dele entraria duas vezes no
 * total do topo, e a tela bateria com a antiga em todo lugar menos no número
 * que mais se olha.
 */
const rollUp = roots => {
  const ordered = []
  const stack = [...roots]
  while (stack.length) {
    const node = stack.pop()
    ordered.push(node)
    node.children.forEach(child => stack.push(child))
  }

  for (let i = ordered.length - 1; i >= 0; i -= 1) {
    const node = ordered[i]
    // A semente é uma cópia, não o próprio `own`: sem isso os dois apontariam
    // para o mesmo objeto em nó sem filhos, e quem mexesse no subtotal mexeria
    // na produção própria do consultor sem perceber.
    node.totals = node.children.reduce(
      (totals, child) => addTotals(totals, child.totals),
      addTotals(emptyTotals(), node.own),
    )

    // A EQUIPE DIRETA: o líder mais quem responde a ele, sem descer mais.
    //
    // §27 do briefing pede os três recortes lado a lado — "consultar o total da
    // equipe direta; consultar o total da estrutura completa" — e este é o do
    // meio. Some `own` dos filhos, e não `totals`: `totals` traria as
    // subestruturas junto e seria a estrutura completa outra vez.
    node.directTotals = node.children.reduce(
      (totals, child) => addTotals(totals, child.own),
      addTotals(emptyTotals(), node.own),
    )
  }
}

/**
 * Monta a árvore a partir das linhas e do esqueleto.
 *
 * ÓRFÃOS: 640 pais não têm linha própria na tabela — não são donos de rede
 * ativa, ou o recorte de data e usuário os cortou. Jogar esses nós na raiz
 * achataria a árvore num ponto arbitrário, e o subtotal do líder perderia gente
 * que é dele. Por isso o `path` vem inteiro do backend: sobe-se até o ancestral
 * mais profundo que ESTIVER na resposta, e só vira raiz quem não tem nenhum.
 *
 * @param {Array} rows linhas de /table
 * @param {Array} structureNodes nós de /structure
 * @returns {{roots: Array, byKey: Object, orphans: number, cycles: number}}
 */
/**
 * A estrutura indexada para ser consultada POR LINHA.
 *
 * A linha de `/table` é chaveada por (consultor, franquia digital) e o nó de
 * `/structure` também — e eu escrevi no plano que "é a mesma chave da linha da
 * tabela". **É a mesma forma e não é o mesmo valor**, e a diferença achata a
 * árvore inteira.
 *
 * `/table` emite uma linha por franquia em que a pessoa faturou, incluindo as
 * que vêm do segundo passe de pagamentos via `contract_networks`. A estrutura
 * tem UM nó por consultor, na franquia da rede ATIVA dele.
 *
 * Medido em junho/2026, com as 2.701 linhas do recorte amplo:
 *
 *   1.979 linhas casam pela chave exata
 *     722 não casam — e **450 dessas são de consultor que ESTÁ na estrutura**,
 *         só em outra franquia (o consultor 3 fatura na 47 e tem rede na 15)
 *
 * Casando só pela chave exata, essas 450 viravam raiz: a árvore tinha **1.292
 * raízes onde o backend diz 8** e profundidade 4 onde ele diz 7. Na tela isso
 * lia como uma lista alfabética de 2.701 nomes com acordeão morto — e o
 * acordeão não estava morto, é que quase ninguém tinha filho. Achado
 * renderizando; nenhum teste de unidade pegava, porque todos os meus casos
 * tinham as duas pontas na mesma franquia.
 *
 * Regra: chave exata primeiro, que é o vínculo específico; na falta, o nó do
 * consultor. Quando o consultor tem mais de um nó — medido: um só na base, o
 * 1586 —, vence o de maior `level`, que é a rede em que ele está mais alto.
 */
const indexStructure = structureNodes => {
  const porChave = new Map()
  const porConsultor = new Map()

  ;(structureNodes || []).forEach(item => {
    const consultor = toNumber(item.consultant_id)
    porChave.set(`${consultor}:${toNumber(item.digital_franchise_id)}`, item)

    const atual = porConsultor.get(consultor)
    if (!atual || toNumber(item.level) > toNumber(atual.level)) {
      porConsultor.set(consultor, item)
    }
  })

  const get = (consultantId, digitalFranchiseId) => porChave
    .get(`${toNumber(consultantId)}:${toNumber(digitalFranchiseId)}`)
    || porConsultor.get(toNumber(consultantId))
    || null

  return { get, exactSize: porChave.size, consultantSize: porConsultor.size }
}

/**
 * ORDEM dos irmãos: faturamento primeiro, nome como desempate.
 *
 * Era por nome, e isso enterrava a tela. Medido em junho/2026: das 281 raízes,
 * **4 têm filho e 6 têm valor**; as outras 275 são linhas sem estrutura, folhas
 * e zeradas — e caem no começo do alfabeto ("ae testando", "alex teste 123").
 * As 30 primeiras linhas da tela eram 30 zeradas e 30 folhas, e a raiz
 * "FuturoCorp", com 533 filhos e R$ 803.417,92 dos R$ 865.026,52 do período,
 * ficava soterrada.
 *
 * Numa tela de faturamento, a ordem tem de responder à pergunta da tela. Isto
 * não move nenhum número: a soma das raízes é a mesma em qualquer ordem, e o
 * total do topo nem passa pela árvore. Só muda o que se vê sem rolar.
 *
 * A `outline` (a numeração de posição) segue esta ordem, que é coerente com ela
 * ser posição NESTE recorte e não código oficial de ninguém.
 *
 * Mora no módulo, e não dentro de `buildTree`, porque `findNodes` usa a MESMA
 * ordem: as N linhas de um consultor aparecem na ordem em que a árvore as
 * mostra, e a primeira delas é a principal.
 */
const byValueThenName = (first, second) => {
  const diff = toNumber(second.totals.total_contract_amount)
    - toNumber(first.totals.total_contract_amount)
  if (diff) {
    return diff
  }
  return String(first.name).localeCompare(String(second.name), 'pt-BR')
}

const buildTree = (rows, structureNodes) => {
  const nodes = new Map()

  const safeRows = rows || []
  safeRows.forEach(row => {
    const key = rowKey(row)
    nodes.set(key, {
      key,
      consultantId: toNumber(row.consultant_id || row.id),
      digitalFranchiseId: toNumber(row.digital_franchise_id),
      name: row.name || row.consultant_name || '',
      leaderFranchiseName: row.leader_franchise_name || '',
      row,
      parentKey: null,
      children: [],
      depth: 0,
      outline: '',
      roleId: null,
      roleName: null,
      own: addTotals(emptyTotals(), row),
      directTotals: emptyTotals(),
      totals: emptyTotals(),
    })
  })

  const structureIndex = indexStructure(structureNodes)

  // Nós por CONSULTOR. A relação estrutural é entre pessoas; a franquia é da
  // linha. Montar a chave do pai com a franquia do FILHO — como estava — só
  // fecha a aresta quando líder e liderado faturam na mesma franquia, e é a
  // segunda metade do achatamento.
  const nodesByConsultant = new Map()
  nodes.forEach(node => {
    if (!nodesByConsultant.has(node.consultantId)) {
      nodesByConsultant.set(node.consultantId, [])
    }
    nodesByConsultant.get(node.consultantId).push(node)
  })

  /**
   * A linha de um ancestral, para pendurar `node` embaixo dela.
   *
   * Preferência: a linha do ancestral na MESMA franquia do filho, que é o
   * vínculo específico; na falta, a linha de maior faturamento dele, que é a
   * principal e a que a pessoa reconhece na tela.
   *
   * Nunca devolve linha do MESMO consultor do filho: pendurar alguém embaixo de
   * outra linha de si mesmo somaria a produção dele duas vezes no próprio
   * subtotal, que é exatamente o que o roll-up promete não fazer.
   */
  const ancestorNode = (ancestorId, node) => {
    const id = toNumber(ancestorId)
    if (!id || id === node.consultantId) {
      return null
    }

    const candidatos = nodesByConsultant.get(id) || []
    if (!candidatos.length) {
      return null
    }

    const mesmaFranquia = candidatos
      .find(item => item.digitalFranchiseId === node.digitalFranchiseId)
    if (mesmaFranquia) {
      return mesmaFranquia
    }

    return candidatos.reduce((maior, item) => (
      toNumber(item.row.total_contract_amount) > toNumber(maior.row.total_contract_amount)
        ? item
        : maior
    ))
  }

  let orphans = 0

  nodes.forEach(node => {
    const structure = structureIndex.get(node.consultantId, node.digitalFranchiseId)
    if (!structure) {
      // Linha sem estrutura: acontece com o suplemento de appointment_networks,
      // que traz consultor sem rede. Raiz, e não sumido.
      return
    }

    // O cargo vem do esqueleto e fica no nó: a tabela mostra ao lado do nome, e
    // a matriz de filtros recorta por ele.
    node.roleId = toNumber(structure.consultant_role_id) || null
    node.roleName = structure.consultant_role_name || null

    const pai = structure.parent_consultant_id != null
      ? ancestorNode(structure.parent_consultant_id, node)
      : null

    if (pai) {
      node.parentKey = pai.key
      return
    }

    if (structure.parent_consultant_id != null) {
      orphans += 1
    }

    // De trás para frente: o ancestral mais PROFUNDO que existe no recorte.
    const path = Array.isArray(structure.path) ? structure.path : []
    for (let i = path.length - 1; i >= 0; i -= 1) {
      const ancestral = ancestorNode(path[i], node)
      if (ancestral) {
        node.parentKey = ancestral.key
        return
      }
    }
  })

  // Ciclo não deveria existir — o backend prova que não existe nos dados de
  // hoje. Mas uma árvore montada no cliente que trava o navegador é pior do que
  // uma árvore com um galho a menos, e o dado vem de uma réplica Kafka sem DLQ.
  let cycles = 0
  nodes.forEach(node => {
    const visited = new Set([node.key])
    let cursor = node.parentKey
    while (cursor) {
      if (visited.has(cursor)) {
        nodes.get(node.key).parentKey = null
        cycles += 1
        break
      }
      visited.add(cursor)
      cursor = nodes.has(cursor) ? nodes.get(cursor).parentKey : null
    }
  })

  const roots = []
  nodes.forEach(node => {
    if (node.parentKey && nodes.has(node.parentKey)) {
      nodes.get(node.parentKey).children.push(node)
    } else {
      roots.push(node)
    }
  })

  // A ordem dos irmãos é `byValueThenName`, no topo do módulo — ela também
  // define qual é a linha PRINCIPAL de quem tem várias (ver findNode).
  // rollUp ANTES de ordenar: a ordem é por subtotal, e é o rollUp que o calcula.
  rollUp(roots)

  roots.sort(byValueThenName)
  nodes.forEach(node => node.children.sort(byValueThenName))

  // E a numeração posicional por último, porque ela é posição na ordem final.
  assignPositions(roots)

  const byKey = {}
  nodes.forEach((node, key) => {
    byKey[key] = node
  })

  return {
    roots, byKey, orphans, cycles,
  }
}

/** Soma reta de linhas, sem árvore. O total geral da tela sai daqui. */
const sumRows = rows => (rows || []).reduce((totals, row) => addTotals(totals, row), emptyTotals())

/**
 * TODOS os nós de um consultor, na ordem em que a árvore os mostra.
 *
 * A chave do nó é `consultor:franquia` (ver `rowKey`), então quem fatura em
 * mais de uma franquia tem mais de um nó — 429 de 1.987 consultores em
 * junho/2026. Quem pergunta "onde está fulano" quase sempre quer TODOS, porque
 * a produção dele é a soma das linhas, não a de uma delas.
 *
 * Desce pelos filhos de um nó já encontrado de propósito: `ancestorNode` impede
 * que um nó de X seja filho DIRETO de outro nó de X, mas não impede que seja
 * neto. Não acontece na base de hoje (medido: 0 casos), e ainda assim parar aí
 * seria perder linha por uma garantia que a construção não dá.
 */
const findNodes = (roots, consultantId) => {
  const alvo = toNumber(consultantId)
  if (!alvo) {
    return []
  }

  const achados = []
  const stack = [...(roots || [])]
  while (stack.length) {
    const node = stack.pop()
    if (node.consultantId === alvo) {
      achados.push(node)
    }
    node.children.forEach(child => stack.push(child))
  }

  return achados.sort(byValueThenName)
}

/**
 * A linha PRINCIPAL de um consultor: a de maior faturamento.
 *
 * Era "o primeiro nó que a pilha encontrar", e isso não era arbitrário — era o
 * PIOR. `buildTree` ordena raízes e irmãos por faturamento DESCRESCENTE, e um
 * DFS com `stack.pop()` percorre na ordem inversa da exibida: entrava primeiro
 * no ramo de MENOR faturamento. Em junho/2026 isso ancorava 26 consultores numa
 * linha de legado zerada e mostrava R$ 0,00 para quem tinha produzido.
 *
 * Agora é a primeira de `findNodes`, que ordena pelo mesmo `byValueThenName`
 * dos irmãos — a mesma preferência por maior faturamento que `ancestorNode`
 * aplica ao escolher a linha de um ancestral quando não há franquia em comum.
 * A tela passa a ter UMA noção de "linha principal".
 *
 * Devolve o nó ORIGINAL, com os filhos e o roll-up intactos — quem for mexer na
 * lista de filhos precisa copiar antes, senão quebra o consolidado que já foi
 * calculado sobre a árvore inteira.
 *
 * ⚠️ Quem quer a PRODUÇÃO do consultor quer `findNodes`, não esta: a produção
 * dele é a soma das linhas. Esta responde "qual linha o representa".
 */
const findNode = (roots, consultantId) => findNodes(roots, consultantId)[0] || null

/**
 * As linhas do consultor que o REPRESENTAM no recorte próprio.
 *
 * Trazer todos os nós conserta o número, mas cria um efeito colateral feio:
 * 375 dos 429 consultores com várias linhas em junho/2026 não produziram em
 * nenhuma delas — são linhas de legado, vindas de redes antigas. "Própria"
 * mostraria três linhas vazias e idênticas onde antes havia uma. Trocar um
 * número errado por três linhas inúteis não é conserto.
 *
 * Fica o nó que tem MOVIMENTO ou que tem EQUIPE:
 *
 *   movimento   qualquer `SUM_FIELDS` diferente de zero, e não só dinheiro —
 *               quem fez EP sem faturar tem linha legítima, e cortar por
 *               `total_contract_amount` apagaria atividade real
 *   equipe      o nó zerado com filhos é o líder de legado, e cortá-lo faria os
 *               liderados sumirem de novo, que é o defeito que acabamos de
 *               consertar
 *
 * É zero-soma por construção: só sai nó que é todo zero E não tem filho. Nenhum
 * total se move.
 *
 * Se nada passar, devolve a linha principal em vez de lista vazia: a pessoa
 * existe, e uma tela dizendo "você não tem linha" seria outra mentira — além de
 * fazer `semLinhaPropria` disparar para quem tem linha.
 */
const temMovimento = totals => SUM_FIELDS.some(campo => toNumber(totals[campo]) !== 0)

const relevantes = nos => {
  const comSinal = nos.filter(no => temMovimento(no.own) || no.children.length)
  return comSinal.length ? comSinal : nos.slice(0, 1)
}

/**
 * O recorte da tabela: QUEM aparece.
 *
 * Os três botões não mudam o que a célula mostra — mudam quem está na lista, e
 * o ponto de ancoragem é o CONSULTOR LOGADO:
 *
 *   own      só a linha dele
 *   direct   ele mais quem responde diretamente a ele
 *   network  tudo o que o recorte trouxe (é a árvore inteira, não a subárvore
 *            dele: um funcionário vê a empresa toda)
 *
 * Quando o usuário não tem linha na árvore — funcionário sem produção própria,
 * por exemplo — "own" e "direct" devolvem vazio, e a tela diz isso em vez de
 * mostrar uma tabela em branco.
 *
 * ÂNCORA PLURAL. Quem fatura em mais de uma franquia tem mais de um nó, e o
 * recorte traz TODOS. Antes vinha um só — o que o DFS alcançasse primeiro — e
 * as outras linhas sumiam da lista E do total, porque `sumContext` e
 * `countContext` somam o que este recorte devolver. Em junho/2026 eram 49
 * consultores vendo a própria produção incompleta (R$ 225.476,87 fora da tela)
 * e 31 líderes perdendo 533 liderados em "Equipe direta", porque
 * `ancestorNode` reparte a equipe entre os nós do líder pela franquia do filho.
 *
 * Normalmente é uma linha só: um PN tem uma franquia ativa. São N na consulta
 * histórica e em quem de fato faturou em mais de uma — e aí o que a tela mostra
 * é o que ele produziu em cada uma.
 */
const scopeRoots = (roots, scope, consultantId) => {
  const safeRoots = roots || []

  // Identidade de propósito, e não uma cópia: `network` é a árvore inteira, e
  // há teste que trava isso com `strictEqual`. Copiar aqui também quebraria o
  // `===` de que a tabela depende para não re-renderizar.
  if (scope !== 'own' && scope !== 'direct') {
    return safeRoots
  }

  const meus = relevantes(findNodes(safeRoots, consultantId))
  if (!meus.length) {
    return []
  }

  // Cópia rasa POR NÓ: a lista muda, os nós não. Mexer no `children` do nó
  // original quebraria o roll-up, que já foi calculado sobre a árvore inteira.
  if (scope === 'own') {
    return meus.map(eu => ({ ...eu, children: [] }))
  }

  // Os `children` dos N nós são disjuntos por construção: em `buildTree` cada
  // nó entra na lista de exatamente um pai, e `parentKey` é escalar. Não há
  // filho repetido para deduplicar.
  return meus.map(eu => ({
    ...eu,
    children: eu.children.map(filho => ({ ...filho, children: [] })),
  }))
}

/**
 * O total da tabela: a soma da produção PRÓPRIA de todo mundo que está no
 * recorte.
 *
 * Sempre `own`, nunca o consolidado, e é isso que o torna consistente com a
 * tabela: uma célula recolhida mostra a subárvore somada e uma expandida mostra
 * só a própria, mas a SOMA do que está na tela é a mesma nos dois casos —
 * porque o que a expansão faz é redistribuir, não criar. O total acompanha essa
 * soma, em qualquer estado de expansão.
 *
 * Somar `totals` das raízes daria o mesmo número no escopo "Estrutura" e
 * contaria gente duas vezes em qualquer outro, então a regra é uma só: `own`.
 */
const sumContext = (roots, scope, consultantId) => {
  let totals = emptyTotals()
  const stack = [...scopeRoots(roots, scope, consultantId)]

  while (stack.length) {
    const node = stack.pop()
    totals = addTotals(totals, node.own)
    node.children.forEach(child => stack.push(child))
  }

  return totals
}

/** Quantas linhas o recorte abrange. */
const countContext = (roots, scope, consultantId) => {
  let total = 0
  const stack = [...scopeRoots(roots, scope, consultantId)]

  while (stack.length) {
    const node = stack.pop()
    total += 1
    node.children.forEach(child => stack.push(child))
  }

  return total
}

/**
 * Achata a árvore na ordem de exibição, respeitando quem está recolhido e quem
 * um filtro de exibição escondeu.
 *
 * `hiddenKeys` esconde a subárvore inteira, e é assim de propósito: um nó só
 * entra lá quando ele E todos os descendentes reprovam o critério (ver
 * kpiFilters.js). Esconder um pai cujo filho passaria sumiria com o filho.
 */
const flattenVisible = (roots, expandedKeys, hiddenKeys) => {
  const visible = []
  const oculto = key => !!(hiddenKeys && hiddenKeys.has(key))
  const walk = nodes => {
    nodes.forEach(node => {
      if (oculto(node.key)) {
        return
      }
      visible.push(node)
      if (node.children.length && expandedKeys && expandedKeys.has(node.key)) {
        walk(node.children)
      }
    })
  }
  walk(roots || [])
  return visible
}

/**
 * As chaves a expandir para abrir a árvore ATÉ um nível.
 *
 * O protótipo troca o par Expandir/Recolher por uma régua — Resumo, 1, 2, 3,
 * Tudo — porque 'expandir tudo' numa estrutura de sete níveis não é leitura,
 * é despejo: some com a linha que a pessoa estava lendo.
 *
 * `nivel` é quantas camadas ficam ABERTAS, contando a partir da raiz:
 *   0 -> nada expandido (Resumo)
 *   1 -> as raízes abrem, os filhos aparecem fechados
 *   N -> N camadas de profundidade abertas
 *
 * Só nó com filho entra: expandir folha não muda nada na tela e sujaria o
 * conjunto, que é comparado por tamanho para habilitar os botões.
 */
const keysUpToLevel = (roots, nivel) => {
  const chaves = new Set()

  if (!nivel || nivel <= 0) {
    return chaves
  }

  const walk = (nodes, profundidade) => {
    if (profundidade >= nivel) {
      return
    }
    (nodes || []).forEach(node => {
      if (!node.children || !node.children.length) {
        return
      }
      chaves.add(node.key)
      walk(node.children, profundidade + 1)
    })
  }

  walk(roots || [], 0)

  return chaves
}
/**
 * A decomposição que o rodapé de reconciliação mostra (§28).
 *
 *   produção própria + equipe direta + subestruturas = estrutura completa
 *
 * É a única coisa na tela que PROVA a ausência de dupla contagem, em vez de
 * pedir que se acredite nela.
 *
 * 'Equipe direta' aqui significa o MESMO que no tile: a produção PRÓPRIA de
 * quem responde direto, líder incluído. O protótipo usava dois significados
 * para palavras parecidas — o tile somava todos os diretos e o rodapé só os
 * que não lideram ninguém —, e duas contas diferentes com rótulos quase iguais
 * na mesma tela é como se fabrica desconfiança no número.
 *
 * Subestruturas é o que sobra de cada filho depois da produção dele: tudo que
 * está ABAIXO da linha direta. Calculado como `totals - own` por filho, então
 * a identidade sai da construção e não de uma asserção.
 */
const reconcile = roots => {
  let propria = emptyTotals()
  let direta = emptyTotals()
  let subestruturas = emptyTotals()
  let total = emptyTotals()

  const abaixoDoFilho = child => {
    const resto = {}
    SUM_FIELDS.forEach(field => {
      resto[field] = toNumber(child.totals[field]) - toNumber(child.own[field])
    })
    return resto
  }

  const lista = roots || []

  lista.forEach(node => {
    propria = addTotals(propria, node.own)
    total = addTotals(total, node.totals)

    const filhos = node.children || []

    filhos.forEach(child => {
      direta = addTotals(direta, child.own)
      subestruturas = addTotals(subestruturas, abaixoDoFilho(child))
    })
  })

  return {
    propria, direta, subestruturas, total,
  }
}
module.exports = {
  SUM_FIELDS,
  addTotals,
  buildTree,
  countContext,
  emptyTotals,
  findNode,
  findNodes,
  flattenVisible,
  indexStructure,
  keysUpToLevel,
  reconcile,
  rowKey,
  scopeRoots,
  sumContext,
  sumRows,
  toNumber,
}
